import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Heading,
  Flex,
  Button,
  Icon,
  Text,
  SimpleGrid,
  Avatar,
  useToast,
  ButtonGroup,
  HStack,
} from '@chakra-ui/react';
import {
  RiCheckboxBlankCircleLine,
  RiCheckboxCircleLine,
  RiDeleteBinLine,
  RiEditLine,
} from 'react-icons/ri';
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { DefaultLayout } from '../../../_layout/DefaultLayout';
import { DeleteConfirmationModal } from '../../../../../components/DeleteConfirmationModal';
import { LinkButton } from '../../../../../components/LinkButton';
import { maskDate } from '../../../../../utils/formatters/handleMask';
import {
  IDetailedAdvertisementCategory,
  showAdvertisementCategoriesService,
} from '../../../../../services/AdvertisementCategories/ShowAdvertisementCategoriesService';
import { deleteAdvertisementCategoriesService } from '../../../../../services/AdvertisementCategories/DeleteAdvertisementCategoriesService';
import { translateError } from '../../../../../utils/errors';

interface IAdvertisementCategory extends IDetailedAdvertisementCategory {
  formattedDate: string;
}

interface ILocationState {
  advertisementCategoryId: string;
}

export const AdvertisementCategoryDetails = (): JSX.Element => {
  const toast = useToast();
  const {
    state: { advertisementCategoryId },
  } = useLocation<ILocationState>();
  const { push } = useHistory();

  const [advertisementCategory, setAdvertisementCategory] =
    useState<IAdvertisementCategory>();
  const [
    isDeleteConfirmationModalVisible,
    setIsDeleteConfirmationModalVisible,
  ] = useState(false);

  useEffect(() => {
    async function loadAdvertisementCategory(): Promise<void> {
      try {
        const advertisementCategoryData =
          await showAdvertisementCategoriesService(advertisementCategoryId);

        const advertisementCategoryWithformattedDate = {
          ...advertisementCategoryData,
          formattedDate: maskDate(advertisementCategoryData.createdAt),
        };

        setAdvertisementCategory(advertisementCategoryWithformattedDate);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao carregar os detalhes da categoria, tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    }

    loadAdvertisementCategory();
  }, [advertisementCategoryId, toast]);

  const handleToggleDeleteConfirmationModal = useCallback(() => {
    setIsDeleteConfirmationModalVisible((prevState) => !prevState);
  }, []);

  const handleDeleteAdvertisementCategory = useCallback(async () => {
    try {
      await deleteAdvertisementCategoriesService(advertisementCategoryId);

      toast({
        title: 'Excluído com sucesso',
        description: 'A categoria foi excluída corretamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
        variant: 'subtle',
        position: 'top-right',
      });

      push('/categories');
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status !== 401) {
        toast({
          title: 'Falha ao excluir',
          description:
            translateError({ message: err.response?.data.message }) ||
            'Ocorreu um erro ao excluir a categoria, tente novamente',
          status: 'error',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      }
    }
  }, [advertisementCategoryId, push, toast]);

  return (
    <DefaultLayout>
      <DeleteConfirmationModal
        isOpen={isDeleteConfirmationModalVisible}
        onClose={handleToggleDeleteConfirmationModal}
        onConfirm={handleDeleteAdvertisementCategory}
      />

      <Box flex="1" borderRadius={8} bg="white" p="8">
        <Flex mb="8" justify="space-between" align="center">
          <Heading size="lg" fontWeight="normal">
            Detalhes da categoria
          </Heading>

          <ButtonGroup>
            <LinkButton
              colorScheme="yellow"
              color="white"
              icon={<Icon as={RiEditLine} fontSize="16" />}
              to={{
                pathname: '/categories/update',
                state: {
                  advertisementCategoryId,
                },
              }}
            >
              Editar
            </LinkButton>

            <Button
              size="sm"
              fontSize="sm"
              colorScheme="red"
              onClick={handleToggleDeleteConfirmationModal}
              leftIcon={<Icon as={RiDeleteBinLine} fontSize="20" />}
            >
              Excluir
            </Button>
          </ButtonGroup>
        </Flex>

        {advertisementCategory && (
          <Flex direction="column">
            <Flex align="center" direction="column">
              <Avatar
                size="2xl"
                name={advertisementCategory.name}
                src={advertisementCategory.iconUrl}
              />

              <Text fontSize="4xl" fontWeight="bold" mt="4">
                {advertisementCategory.name}
              </Text>

              <HStack mt="4">
                <Text>Ativo:</Text>
                {advertisementCategory.isActive ? (
                  <Icon
                    fontSize="3xl"
                    as={RiCheckboxCircleLine}
                    color="green"
                  />
                ) : (
                  <Icon
                    fontSize="3xl"
                    as={RiCheckboxBlankCircleLine}
                    color="gray.400"
                  />
                )}
              </HStack>

              <Text
                textAlign="center"
                fontSize="sm"
                color="gray.500"
                mt="2"
                width="50%"
              >
                {advertisementCategory.description}
              </Text>
            </Flex>

            <SimpleGrid
              minChildWidth="240px"
              spacing="8"
              px="8"
              mt="4"
              w="100%"
            >
              <HStack>
                <Text>Anúncios:</Text>
                <Text color="gray.500">
                  {advertisementCategory.advertisements.length}
                </Text>
              </HStack>

              <HStack>
                <Text>Ordem de exibição:</Text>
                <Text color="gray.500">{advertisementCategory.showOrder}</Text>
              </HStack>

              <HStack>
                <Text>Data de cadastro:</Text>
                <Text color="gray.500">
                  {advertisementCategory.formattedDate}
                </Text>
              </HStack>
            </SimpleGrid>
          </Flex>
        )}
      </Box>
    </DefaultLayout>
  );
};
